import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import OnboardingModal from "@/components/OnboardingModal";
import {
  Wallet, Receipt, AlertTriangle, TrendingDown, TrendingUp, Clock,
  Users, ShieldAlert, Coins, Building, ArrowDownRight, ArrowUpRight, Calendar,
} from "lucide-react";

const LIGHT_BG = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444" };
const LIGHT_LABEL = { green: "Alles im grünen Bereich", yellow: "Vorsicht – einige Punkte beachten", red: "Achtung – jetzt handeln" };
const LIGHT_DESC = {
  green: "Deine Liquidität ist gesund. Steuer ist gedeckt, Fixkosten passen, keine überfälligen Rechnungen.",
  yellow: "Mindestens eine Kennzahl knirscht – z.B. überfällige Forderungen, knappe Rücklage oder enge Liquidität.",
  red: "Negatives frei verfügbares Geld, überfällige Lieferantenrechnungen oder eine erhebliche USt-Lücke. Bitte sofort prüfen.",
};

function Kpi({ label, value, hint, icon: Icon, testId, accent }) {
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="kpi-label">{label}</span>
        {Icon && (
          <div className="h-7 w-7 rounded-md flex items-center justify-center bg-neutral-100">
            <Icon className="h-3.5 w-3.5 text-neutral-600" />
          </div>
        )}
      </div>
      <div className={`kpi-value ${accent || ""}`} data-testid={`${testId}-value`}>{value}</div>
      {hint && <div className="kpi-sub">{hint}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const currency = data?.currency || "EUR";

  useEffect(() => {
    Promise.all([api.get("/dashboard"), api.get("/alerts")])
      .then(([d, a]) => {
        setData(d.data);
        setAlerts(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-neutral-500" data-testid="dashboard-loading">Lade Cockpit…</div>;
  }
  if (!data) return null;

  const tl = data.traffic_light;
  const bd = data.breakdown;
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <OnboardingModal />
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Finanz-Cockpit</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Wie viel Geld ist <span style={{ color: "var(--tenant-primary)" }}>wirklich frei</span> verfügbar?
          </h1>
          <p className="text-neutral-500 mt-2 text-sm max-w-2xl">
            Dein Kontostand minus alles, was bereits für Steuer, Lieferanten, Fixkosten und Projekte verplant ist.
          </p>
        </div>
        <div className="text-xs text-neutral-500 flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          {new Date().toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* HERO KPI Frei verfügbares Geld */}
      <Card className="border-neutral-200 overflow-hidden" data-testid="hero-kpi-card">
        <div className="grid md:grid-cols-5 gap-0">
          <div className="md:col-span-3 p-8 lg:p-10 border-b md:border-b-0 md:border-r border-neutral-200">
            <div className="flex items-center gap-2 mb-4">
              <span className="dot" style={{ backgroundColor: LIGHT_BG[tl] }} data-testid="traffic-light"></span>
              <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">{LIGHT_LABEL[tl]}</span>
            </div>
            <div className="text-sm text-neutral-500 mb-2">Frei verfügbares Geld</div>
            <div
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-bold"
              style={{ color: data.available_money < 0 ? "#EF4444" : "#0A0A0A" }}
              data-testid="hero-kpi-available-cash"
            >
              {fmtCurrency(data.available_money, currency)}
            </div>
            <p className="text-sm text-neutral-600 mt-4 max-w-md leading-relaxed">{LIGHT_DESC[tl]}</p>
          </div>
          <div className="md:col-span-2 p-8 lg:p-10 bg-neutral-50">
            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-4">Was steckt drin?</div>
            <BreakdownRow label="Operatives Guthaben" value={bd.operating_balance} positive currency={currency} />
            <BreakdownRow label="USt-Rücklage Lücke" value={-bd.vat_reserve_gap} currency={currency} />
            <BreakdownRow label="Offene Lieferanten" value={-bd.open_supplier_invoices} currency={currency} />
            <BreakdownRow label="Fixkosten 30 Tage" value={-bd.fixed_costs_30d} currency={currency} />
            <BreakdownRow label="Reservierte Projektkosten" value={-bd.reserved_project_costs} currency={currency} />
            <BreakdownRow label="Media Budget noch offen" value={-bd.media_budget_pending} currency={currency} />
            <BreakdownRow label="Sicherheitsrücklage" value={-bd.safety_reserve} currency={currency} />
            <div className="border-t border-neutral-300 mt-3 pt-3 flex justify-between items-baseline">
              <div className="text-sm font-semibold text-neutral-900">Frei verfügbar</div>
              <div
                className="font-display font-semibold text-lg"
                style={{ color: data.available_money < 0 ? "#EF4444" : "#0A0A0A" }}
              >
                {fmtCurrency(data.available_money, currency)}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Aktueller Kontostand"
          value={fmtCurrency(k.total_balance, currency)}
          hint={`Operativ ${fmtCurrency(k.operating_balance, currency)} · Steuer ${fmtCurrency(k.tax_account_balance, currency)}`}
          icon={Wallet}
          testId="kpi-total-balance"
        />
        <Kpi
          label="USt-Rücklage empfohlen"
          value={fmtCurrency(k.vat_reserve_recommended, currency)}
          hint={k.vat_reserve_gap > 0 ? `Lücke ${fmtCurrency(k.vat_reserve_gap, currency)}` : "Vollständig gedeckt"}
          icon={Receipt}
          testId="kpi-vat-reserve"
        />
        <Kpi
          label="Offene Kundenzahlungen"
          value={fmtCurrency(k.open_receivables, currency)}
          hint={k.overdue_receivables > 0 ? `Davon ${fmtCurrency(k.overdue_receivables, currency)} überfällig` : "Nichts überfällig"}
          icon={Users}
          testId="kpi-open-receivables"
        />
        <Kpi
          label="Offene Lieferanten"
          value={fmtCurrency(k.open_supplier_invoices, currency)}
          hint={k.overdue_supplier_invoices > 0 ? `Davon ${fmtCurrency(k.overdue_supplier_invoices, currency)} überfällig` : "Im Plan"}
          icon={Building}
          testId="kpi-open-suppliers"
        />
        <Kpi
          label="Monatliche Fixkosten"
          value={fmtCurrency(k.monthly_fixed_costs, currency)}
          hint={`Break-Even ca. ${fmtCurrency(k.break_even_monthly, currency)} / Monat`}
          icon={TrendingDown}
          testId="kpi-monthly-fixed"
        />
        <Kpi
          label="Cash-In 30 Tage"
          value={fmtCurrency(k.cash_in_30d, currency)}
          hint="Erwartete Eingänge"
          icon={ArrowDownRight}
          testId="kpi-cash-in"
        />
        <Kpi
          label="Cash-Out 30 Tage"
          value={fmtCurrency(k.cash_out_30d, currency)}
          hint="Fix + Lieferanten"
          icon={ArrowUpRight}
          testId="kpi-cash-out"
        />
        <Kpi
          label="Liquiditätsreichweite"
          value={k.runway_months !== null ? `${k.runway_months} Monate` : "—"}
          hint={`Deckungsbeitrag laufender Projekte: ${fmtCurrency(k.project_margin_total, currency)}`}
          icon={Clock}
          testId="kpi-runway"
        />
      </div>

      {/* Warnings & overdue */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-neutral-200 bg-white p-6" data-testid="alerts-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" /> Warnungen & Empfehlungen
            </h3>
            <span className="text-xs text-neutral-500">{alerts.length} aktiv</span>
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center" data-testid="alerts-empty">
              🎉 Keine Warnungen — finanziell läuft alles rund.
            </div>
          ) : (
            <ul className="space-y-2">
              {alerts.map((a, i) => (
                <li
                  key={i}
                  data-testid={`alert-${i}`}
                  className="rounded-lg border border-neutral-200 px-4 py-3 flex gap-3 items-start hover:border-neutral-300 transition-colors"
                >
                  <span
                    className="dot mt-1.5 shrink-0"
                    style={{
                      backgroundColor:
                        a.severity === "red" ? "#EF4444" : a.severity === "yellow" ? "#F59E0B" : "#3B82F6",
                    }}
                  />
                  <div>
                    <div className="font-medium text-neutral-900 text-sm">{a.title}</div>
                    <div className="text-sm text-neutral-600 mt-0.5">{a.message}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="overdue-panel">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Überfällig
          </h3>
          <div className="mb-5">
            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-2">Kunden</div>
            {data.overdue.customers.length === 0 ? (
              <div className="text-xs text-neutral-500">Keine überfälligen Forderungen</div>
            ) : (
              data.overdue.customers.map((o) => (
                <div key={o.id} className="flex justify-between items-baseline text-sm py-1.5 border-b border-neutral-100 last:border-0">
                  <span className="text-neutral-700">{o.invoice_number}</span>
                  <span className="font-medium text-red-600">{fmtCurrency(o.amount, currency)}</span>
                </div>
              ))
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-2">Lieferanten</div>
            {data.overdue.suppliers.length === 0 ? (
              <div className="text-xs text-neutral-500">Keine überfälligen Verbindlichkeiten</div>
            ) : (
              data.overdue.suppliers.map((o) => (
                <div key={o.id} className="flex justify-between items-baseline text-sm py-1.5 border-b border-neutral-100 last:border-0">
                  <span className="text-neutral-700 truncate">{o.supplier_name}</span>
                  <span className="font-medium text-red-600">{fmtCurrency(o.amount, currency)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Media disclaimer */}
      {k.media_received_no_vat > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 items-start" data-testid="media-disclaimer">
          <Coins className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Hinweis Media Budget:</strong> Du hast {fmtCurrency(k.media_received_no_vat, currency)} Media Budget von Kunden erhalten – das ist ein <strong>durchlaufender Posten</strong> und gehört dir nicht. Es ist bereits aus dem frei verfügbaren Geld herausgerechnet.
          </div>
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ label, value, positive, currency }) {
  const isPositive = value >= 0;
  return (
    <div className="flex justify-between items-baseline py-1.5 text-sm">
      <span className="text-neutral-600">{label}</span>
      <span className={`font-medium tabular-nums ${isPositive ? "text-neutral-900" : "text-red-600"}`}>
        {value >= 0 && positive ? "+" : ""}
        {fmtCurrency(value, currency)}
      </span>
    </div>
  );
}
